import type { ConversationSummaryDto } from '@carbonwebmail/contracts'
import { when } from '../../ui/format.js'

/**
 * The conversations this organisation has had with the designer.
 *
 * **Collapsed to a narrow strip rather than removed.** Getting an email right
 * takes a dozen turns, and the two panes beside this one — the conversation and
 * the preview — are where the work happens. A list that could not be put away
 * would be taking width from them permanently; one that vanished entirely would
 * leave nothing to press to get it back.
 *
 * Open, it is names and when they were last touched. That is enough to choose
 * one, and it is why the title is the first thing that was asked for rather
 * than "Conversation 7".
 */
export function Conversations({
  conversations,
  open,
  current,
  listed,
  onOpen,
  onForget,
  onNew,
  onToggle,
}: {
  readonly conversations: readonly ConversationSummaryDto[]
  readonly open: boolean
  /** Which one is on screen, so the list can say so. */
  readonly current: string | undefined
  /** Whether the list came back at all. An empty list is not a failed one. */
  readonly listed: boolean
  readonly onOpen: (conversationId: string) => void
  readonly onForget: (conversationId: string) => void
  readonly onNew: () => void
  readonly onToggle: () => void
}) {
  if (!open) {
    return (
      <div className="flex-none border-line border-r pr-2">
        <button
          aria-label="Show saved conversations"
          className="rounded-pill border border-line px-2 py-2 font-semibold text-[13px] text-t3 hover:text-t1"
          onClick={onToggle}
          type="button"
          title="Saved conversations"
        >
          »
        </button>
      </div>
    )
  }

  return (
    <aside className="flex w-60 flex-none flex-col border-line border-r pr-3">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="font-bold text-[13px] text-t1">Conversations</h2>
        <button
          aria-label="Hide saved conversations"
          className="rounded-pill px-2 py-1 font-semibold text-[13px] text-t3 hover:text-t1"
          onClick={onToggle}
          type="button"
        >
          «
        </button>
      </div>

      <button
        className="mb-3 rounded-pill border border-line px-3 py-1.5 font-semibold text-[13px] text-t2 hover:text-t1"
        onClick={onNew}
        type="button"
      >
        New conversation
      </button>

      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
        {/*
          Nothing at all when the list could not be read. "No conversations yet"
          is a claim about what somebody has done, and a request that failed is
          not evidence of it — the same lie the boards kept telling.
        */}
        {conversations.length === 0 ? (
          listed ? (
            <p className="px-2 py-3 text-[12px] text-t3">
              Nothing saved yet. Ask for a design and it will be kept here.
            </p>
          ) : null
        ) : (
          conversations.map((one) => (
            <div
              className={`group flex items-center gap-1 rounded-field px-2 py-1.5 ${
                one.conversationId === current ? 'bg-surface-2' : 'hover:bg-surface-2'
              }`}
              key={one.conversationId}
            >
              <button
                className="min-w-0 flex-1 text-left"
                onClick={() => onOpen(one.conversationId)}
                type="button"
              >
                <span className="block truncate font-medium text-[13px] text-t1">{one.title}</span>
                <span className="block text-[11px] text-t3">{when(one.updatedAt)}</span>
              </button>
              <button
                aria-label={`Forget ${one.title}`}
                className="rounded-pill px-1.5 text-t3 opacity-0 transition-opacity hover:text-danger focus:opacity-100 group-hover:opacity-100"
                onClick={() => onForget(one.conversationId)}
                type="button"
              >
                ×
              </button>
            </div>
          ))
        )}
      </div>
    </aside>
  )
}
